import React, { FC, useEffect } from 'react';

import { Spinner } from '@chakra-ui/react';
import { filter, toArray } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';

import { getIcon, PageHeaderWithIcon } from 'components/DataDisplay';
import { Form } from 'components/DataEntry';
import { SyncRestartIcon } from 'components/Icons/ReactCustomIcons/SyncRestartIcon';
import { Modal } from 'components/Overlay';
import { useToasts } from 'components/Toast';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { selectApp } from 'containers/App/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { useSkipTags } from 'containers/Visibility/Data/Components/DataTable/SyncLabels/useSkipTags';
import { dataSourceTypeOptions } from 'containers/Visibility/Data/Components/DataTable/SyncLabels/utils';
import { selectDataDashboard } from 'containers/Visibility/Data/selectors';
import { actions } from 'containers/Visibility/Data/slice';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const SyncLabels: FC<Props> = props => {
  const { isOpen, onClose } = props;
  const { syncLabels } = useSelector(selectDataDashboard);
  const { cloudAccounts, accessBoundary } = useSelector(selectApp);
  const { accountId } = useAccessBoundary();
  const { environment } = accessBoundary;
  const dispatch = useDispatch();
  const [dataSourceType, setDataSourceType] = React.useState(
    dataSourceTypeOptions[0],
  );
  const [account, setAccount] = React.useState<Record<string, any>>({});
  const [accountOptions, setAccountOptions] = React.useState<
    Record<string, any>[]
  >([]);
  const { accountMapping } = useCloudAccountId();
  const { errorToast } = useToasts();
  // Skip tags: columns already carrying any of these customer tags are not
  // tagged by the sync. Saved per account before the sync is triggered.
  const skipTags = useSkipTags(account?.value);
  const [isSaving, setIsSaving] = React.useState(false);

  useEffect(() => {
    let options: Record<string, any>[] = [];
    if (!!accountId) {
      const val = accountMapping?.[accountId];
      if (!val || val?.cloud_type !== dataSourceType?.value) return;
      options = [
        {
          label: val.name + ' (Account ID: ' + val.cloud_account_id + ')',
          value: val.uuid,
          icon: getIcon(val.cloud_type!),
        },
      ];
    } else
      options = map(
        filter(
          toArray(environment?.account_ids),
          o => accountMapping?.[o]?.cloud_type === dataSourceType?.value,
        ),
        a => {
          const val = accountMapping?.[a];
          return {
            label: val.name + ' (Account ID: ' + val.cloud_account_id + ')',
            value: val.uuid,
            icon: getIcon(val.cloud_type!),
          };
        },
      );
    setAccountOptions(options);
    setAccount(options[0]);
  }, [
    cloudAccounts.data,
    dataSourceType,
    accountMapping,
    environment,
    accountId,
  ]);

  const handleSubmit = async () => {
    // Save the skip-tag selection first so the queued sync uses it.
    setIsSaving(true);
    const saveError = await skipTags.save();
    setIsSaving(false);
    if (saveError) {
      errorToast({
        title: 'Unable to save skip tags.',
        description: saveError,
      });
      return;
    }
    dispatch(
      actions.syncLabels({
        q: { accountId: account?.value },
        onSuccess: () => onClose(),
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <PageHeaderWithIcon label="Sync Labels" icon={<SyncRestartIcon />} />
      }
      body={
        <Form
          schema={{
            sourceType: {
              type: 'react-select',
              label: 'Data Source Type',
              options: dataSourceTypeOptions,
              value: dataSourceType,
              onChange: setDataSourceType,
              isDisabled: true,
            },
            accounts: {
              type: 'react-select',
              // Was mislabelled 'Data Source Type'.
              label: 'Account',
              options: accountOptions,
              value: account,
              onChange: setAccount,
            },
            skipTags: {
              type: 'react-select',
              label: 'Skip columns that already have any of these tags',
              options: skipTags.options,
              value: skipTags.selected,
              onChange: selected => skipTags.setSelected(selected ?? []),
              isMulti: true,
              isLoading: skipTags.isLoading,
              isDisabled:
                !account?.value ||
                skipTags.isLoading ||
                !skipTags.settingsLoaded,
              // The tags call queries Snowflake and can take a while; show a
              // visible spinner + message instead of an empty-looking field.
              placeholder: skipTags.isLoading
                ? 'Loading tags from Snowflake…'
                : 'No tags selected',
              valueContainerIcon: skipTags.isLoading ? (
                <Spinner size="xs" color="primary" />
              ) : undefined,
              helperText: skipTags.isLoading
                ? 'Fetching tags from Snowflake. This can take up to a minute.'
                : skipTags.tagsError
                ? skipTags.tagsError
                : !skipTags.isLoading &&
                  !!account?.value &&
                  !skipTags.settingsLoaded
                ? 'Unable to load saved skip tags; the sync will use the existing settings.'
                : 'Tags set on the column, or inherited from its table, schema or database, count. ' +
                  'Ariksa tags already on those columns are removed. Account-wide syncs may take ' +
                  'up to 2 hours to notice newly added tags.',
            },
          }}
          buttonOptions={{
            submit: {
              name: 'OK',
              isVisible: true,
              isDisabled:
                syncLabels.isLoading ||
                isSaving ||
                skipTags.isLoading ||
                !account?.value,
              isLoading: syncLabels.isLoading || isSaving,
            },
            reset: {
              name: 'CANCEL',
              isVisible: true,
              onClick: () => onClose(),
            },
          }}
          handleSubmit={handleSubmit}
          styles={{ form: { submitButton: { px: 6 } } }}
        />
      }
      size="lg"
    />
  );
};
